import { BlockResponse } from "protos/portal/spatial";
import { DATE_PATH_FORMAT } from "portal/utils/dates";
import { DateTime } from "luxon";
import { HealthLog } from "protos/portal/health";
import {
  JigsawBlocksCrop,
  JigsawBlocksWeed,
  JigsawSpatialMetricBlock,
} from "protos/portal/jigsaw";
import { portalApi } from "portal/state/portalApi";
import { useJigsawMetrics } from "portal/utils/hooks/useJigsawMetrics";
import { useLazyPopups } from "portal/utils/hooks/useApiPopups";
import { useMemo } from "react";
import { useMemoAsync } from "portal/utils/hooks/useMemoAsync";
import { useTranslation } from "react-i18next";

export interface JigsawRobotHistory {
  type: "jigsaw";
  jigsawBlocks: JigsawSpatialMetricBlock[] | undefined;
  crops: JigsawBlocksCrop[];
  weeds: JigsawBlocksWeed[];
}

interface OldRobotHistory {
  type: "old";
  blocks: BlockResponse[] | undefined;
}

export type RobotBlockHistory = JigsawRobotHistory | OldRobotHistory;

type RobotHistoryData = RobotBlockHistory & {
  logs: HealthLog[] | undefined;
};

type RobotHistory = RobotHistoryData & {
  hasBlocks: boolean | undefined;
  hasLogs: boolean | undefined;
  isLoading: boolean;
};

export const historyHasBlocks = (
  blockHistory: RobotBlockHistory | undefined
): boolean => {
  if (blockHistory === undefined) {
    return false;
  }
  const hasJigsaw =
    blockHistory.type === "jigsaw" &&
    blockHistory.jigsawBlocks !== undefined &&
    blockHistory.jigsawBlocks.length > 0;

  const hasOld =
    blockHistory.type === "old" &&
    blockHistory.blocks !== undefined &&
    blockHistory.blocks.length > 0;

  return hasJigsaw || hasOld;
};

export const useRobotHistory = (
  serial: string | undefined,
  jobId?: string,
  inputDate?: DateTime
): RobotHistory => {
  const { i18n } = useTranslation();
  const useJigsaw = useJigsawMetrics();
  const date = useMemo(
    () => (inputDate ?? DateTime.local()).toFormat(DATE_PATH_FORMAT),
    [inputDate]
  );

  const [getJobHistory] = useLazyPopups(
    portalApi.endpoints.getJobHistory.useLazyQuery(),
    {
      errorVariant: "warning",
    }
  );
  const [getJobBlocks] = useLazyPopups(
    portalApi.endpoints.getJobBlocks.useLazyQuery(),
    {
      errorVariant: "warning",
    }
  );
  const [getJobJigsawBlocks] = useLazyPopups(
    portalApi.endpoints.getJobJigsawBlocks.useLazyQuery(),
    {
      errorVariant: "warning",
    }
  );
  const [getRobotBlocks] = useLazyPopups(
    portalApi.endpoints.getRobotBlocks.useLazyQuery(),
    {
      errorVariant: "warning",
    }
  );
  const [getRobotJigsawBlocks] = useLazyPopups(
    portalApi.endpoints.getRobotJigsawBlocks.useLazyQuery(),
    {
      errorVariant: "warning",
    }
  );
  const [getRobotHistory] = useLazyPopups(
    portalApi.endpoints.getRobotHistory.useLazyQuery(),
    {
      errorVariant: "warning",
    }
  );
  const [historyData, { isLoading }] = useMemoAsync<RobotHistoryData>(
    async (): Promise<RobotHistoryData> => {
      if (!serial) {
        if (useJigsaw) {
          return {
            type: "jigsaw",
            logs: undefined,
            jigsawBlocks: undefined,
            crops: [],
            weeds: [],
          };
        }
        return {
          type: "old",
          blocks: undefined,
          logs: undefined,
        };
      }
      if (jobId) {
        if (useJigsaw) {
          const jigsawBlockResponse = await getJobJigsawBlocks(
            { jobId, language: i18n.language },
            true
          );
          if (
            jigsawBlockResponse.data &&
            jigsawBlockResponse.data.blocks.length > 0
          ) {
            return {
              type: "jigsaw",
              jigsawBlocks: jigsawBlockResponse.data.blocks,
              logs: [],
              crops: jigsawBlockResponse.data.crops,
              weeds: jigsawBlockResponse.data.weeds,
            };
          }
        } else {
          const blockResponse = await getJobBlocks({ jobId }, true);
          if (blockResponse.data && blockResponse.data.blocks.length > 0) {
            return {
              type: "old",
              blocks: blockResponse.data.blocks,
              logs: [],
            };
          }
        }
        // get logs if there are no blocks
        const logResponse = await getJobHistory({ jobId }, true);

        if (useJigsaw) {
          return {
            type: "jigsaw",
            logs: logResponse.data?.logs ?? [],
            jigsawBlocks: [],
            crops: [],
            weeds: [],
          };
        }
        return {
          type: "old",
          blocks: [],
          logs: logResponse.data?.logs ?? [],
        };
      } else {
        if (useJigsaw) {
          const jigsawBlockResponse = await getRobotJigsawBlocks(
            {
              language: i18n.language,
              serial,
              date,
            },
            true
          );
          if (
            jigsawBlockResponse.data &&
            jigsawBlockResponse.data.blocks.length > 0
          ) {
            return {
              type: "jigsaw",
              jigsawBlocks: jigsawBlockResponse.data.blocks,
              logs: [],
              crops: jigsawBlockResponse.data.crops,
              weeds: jigsawBlockResponse.data.weeds,
            };
          }
        } else {
          const blockResponse = await getRobotBlocks(
            {
              serial,
              date,
            },
            true
          );
          if (blockResponse.data && blockResponse.data.blocks.length > 0) {
            return {
              type: "old",
              blocks: blockResponse.data.blocks,
              logs: [],
            };
          }
        }
        // get logs if there are no blocks
        const logResponse = await getRobotHistory(
          {
            serial,
            date,
          },
          true
        );
        if (useJigsaw) {
          return {
            type: "jigsaw",
            jigsawBlocks: [],
            logs: logResponse.data?.logs ?? [],
            crops: [],
            weeds: [],
          };
        }
        return {
          type: "old",
          blocks: [],
          logs: logResponse.data?.logs ?? [],
        };
      }
    },
    [
      date,
      getJobBlocks,
      getJobHistory,
      getJobJigsawBlocks,
      getRobotBlocks,
      getRobotHistory,
      getRobotJigsawBlocks,
      i18n.language,
      jobId,
      serial,
      useJigsaw,
    ],
    useJigsaw
      ? {
          type: "jigsaw",
          logs: undefined,
          jigsawBlocks: undefined,
          crops: [],
          weeds: [],
        }
      : { type: "old", logs: undefined, blocks: undefined }
  );

  if (historyData.type === "jigsaw") {
    const blocks = historyData.jigsawBlocks;
    return {
      ...historyData,
      hasBlocks:
        blocks === undefined
          ? undefined
          : Array.isArray(blocks) && blocks.length > 0,
      hasLogs:
        historyData.logs === undefined
          ? undefined
          : Array.isArray(historyData.logs) && historyData.logs.length > 0,
      isLoading,
    };
  }
  const blocks = historyData.blocks;

  return {
    ...historyData,
    hasBlocks:
      blocks === undefined
        ? undefined
        : Array.isArray(blocks) && blocks.length > 0,
    hasLogs:
      historyData.logs === undefined
        ? undefined
        : Array.isArray(historyData.logs) && historyData.logs.length > 0,
    isLoading,
  };
};
